// Task 2 · "Het slot" (opdrachten-browsertechniek.md #17).
// A rotary combination lock. Turn it with a finger, or (on a phone) by turning the phone itself
// flat on the table (DeviceOrientation). Ticks are sound, plus vibration on Android.
// The code is something the player found earlier: the year above the door.

import { sfx, vibrate } from '../audio.js';
import { backstage } from '../backstage.js';
import { openSheet } from '../sheet.js';
import { el, isTouch, sleep } from '../util.js';

export interface LockResult { method: 'drag' | 'motion' | 'typed'; attempts: number }

const SVG_NS = 'http://www.w3.org/2000/svg';
const svg = <K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}) => {
  const n = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
  return n;
};

type OrientationPermission = { requestPermission?: () => Promise<'granted' | 'denied'> };

export async function runLock(code: string): Promise<LockResult> {
  const sheet = openSheet({
    label: 'Opdracht 2 van 3',
    title: 'Het slot',
    quote: '“Haar laatste map zit achter een draaislot. Vier cijfers. De code ken je al.”',
  });

  // --- Dial -------------------------------------------------------------------------------
  const C = 130;
  const dial = svg('svg', { viewBox: '0 0 260 260', class: 'dial', role: 'slider', 'aria-label': 'Draaislot', tabindex: 0 });
  dial.append(
    svg('circle', { cx: C, cy: C, r: 124, fill: '#0e0e10', stroke: '#2a2a2e', 'stroke-width': 2 }),
    svg('path', { d: `M${C - 9} 2 L${C + 9} 2 L${C} 18 Z`, fill: 'var(--signal)' }),
  );
  const face = svg('g', { class: 'dial__face' });
  for (let i = 0; i < 50; i++) {
    const a = (i * 7.2 * Math.PI) / 180;
    const long = i % 5 === 0;
    face.append(svg('line', {
      x1: C + Math.sin(a) * 116, y1: C - Math.cos(a) * 116,
      x2: C + Math.sin(a) * (long ? 102 : 109), y2: C - Math.cos(a) * (long ? 102 : 109),
      stroke: long ? '#85837c' : '#3a3a40', 'stroke-width': long ? 2 : 1,
    }));
  }
  const digits: SVGTextElement[] = [];
  for (let d = 0; d < 10; d++) {
    const a = (d * 36 * Math.PI) / 180;
    const t = svg('text', { x: C + Math.sin(a) * 82, y: C - Math.cos(a) * 82 + 8, 'text-anchor': 'middle', class: 'dial__digit' });
    t.textContent = String(d);
    digits.push(t); face.append(t);
  }
  const knob = svg('circle', { cx: C, cy: C, r: 50, fill: '#18181b', stroke: '#2f2f34', 'stroke-width': 2 });
  for (let i = 0; i < 24; i++) {
    const a = (i * 15 * Math.PI) / 180;
    face.append(svg('line', { x1: C + Math.sin(a) * 42, y1: C - Math.cos(a) * 42, x2: C + Math.sin(a) * 49, y2: C - Math.cos(a) * 49, stroke: '#2a2a2e', 'stroke-width': 2 }));
  }
  const readout = svg('text', { x: C, y: C + 12, 'text-anchor': 'middle', class: 'dial__readout', style: 'font: 500 34px var(--mono); fill: var(--text)' });
  dial.append(face, knob, readout);

  const slots = Array.from({ length: code.length }, () => el('span', {}, '·'));
  const lockEl = el('div.lock', {}, el('div.lock__slots', {}, ...slots), dial);
  const help = el('p.small', { style: 'text-align:center;margin:0' }, 'Draai naar een cijfer en laat los.');
  const motionBtn = el('button.btn.btn--ghost.btn--small', { type: 'button' }, 'Draai met je gsm');
  const typeBtn = el('button.btn.btn--ghost.btn--small', { type: 'button' }, 'Cijfers intikken');
  const modes = el('div.lock__mode', {}, isTouch() && 'DeviceOrientationEvent' in window ? motionBtn : null, typeBtn);
  sheet.body.append(lockEl, help, modes);

  // --- State ------------------------------------------------------------------------------
  let rot = 0;           // degrees
  let entered = '';
  let attempts = 0;
  let method: LockResult['method'] = 'drag';
  let currentDigit = 0;
  let busy = false;
  let resolveDone!: () => void;
  const done = new Promise<void>((r) => (resolveDone = r));

  const digitAt = (r: number) => ((Math.round(-r / 36) % 10) + 10) % 10;
  const render = () => {
    face.setAttribute('transform', `rotate(${rot} ${C} ${C})`);
    // Keep the digits upright while the dial turns
    digits.forEach((t, i) => { const a = (i * 36 * Math.PI) / 180; t.setAttribute('transform', `rotate(${-rot} ${C + Math.sin(a) * 82} ${C - Math.cos(a) * 82})`); });
    const d = digitAt(rot);
    if (d !== currentDigit) { currentDigit = d; sfx.tick(); vibrate(4); }
    readout.textContent = String(d);
    digits.forEach((t, i) => t.classList.toggle('is-active', i === d));
    dial.setAttribute('aria-valuenow', String(d));
    slots.forEach((s, i) => { s.classList.toggle('is-current', i === entered.length); });
  };

  const commit = async () => {
    if (busy || entered.length >= code.length) return;
    entered += String(currentDigit);
    sfx.tick(true); vibrate(15);
    slots[entered.length - 1].textContent = String(currentDigit);
    slots[entered.length - 1].classList.add('is-filled');
    render();
    if (entered.length === code.length) await check(entered);
  };

  const check = async (value: string) => {
    busy = true;
    attempts++;
    if (value === code) {
      lockEl.classList.add('is-open');
      sfx.success(); vibrate([30, 60, 30]);
      sheet.feedback('Klik. Het slot springt open.', 'good');
      await sleep(1100);
      resolveDone();
      return;
    }
    lockEl.classList.add('is-wrong'); sfx.wrong(); vibrate(120);
    sheet.feedback(attempts >= 2 ? 'Nee. Joris: “Het jaartal boven de deur, misschien?”' : 'Nee. Het slot geeft niet mee.', 'bad');
    backstage.log('verhaal', `Slot: fout (${value})`, attempts >= 2 ? 'Na twee pogingen geeft het personage een hint. Geen tijdsdruk, geen game over.' : 'Fout antwoord: het verhaal wacht gewoon.');
    await sleep(700);
    lockEl.classList.remove('is-wrong');
    entered = '';
    slots.forEach((s) => { s.textContent = '·'; s.classList.remove('is-filled'); });
    busy = false;
    render();
  };

  // --- Finger: drag around the centre -------------------------------------------------------
  let dragging = false, lastAngle = 0, moved = 0;
  const angleOf = (e: PointerEvent) => {
    const r = dial.getBoundingClientRect();
    return (Math.atan2(e.clientY - (r.top + r.height / 2), e.clientX - (r.left + r.width / 2)) * 180) / Math.PI;
  };
  dial.addEventListener('pointerdown', (e) => {
    if (busy) return;
    dragging = true; moved = 0; lastAngle = angleOf(e);
    dial.setPointerCapture(e.pointerId);
  });
  dial.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const a = angleOf(e);
    let d = a - lastAngle;
    if (d > 180) d -= 360; if (d < -180) d += 360;
    lastAngle = a; rot += d; moved += Math.abs(d);
    render();
  });
  const release = () => {
    if (!dragging) return;
    dragging = false;
    rot = -currentDigit * 36 + Math.round(rot / 360) * 360; // snap
    render();
    if (moved > 8) void commit();
  };
  dial.addEventListener('pointerup', release);
  dial.addEventListener('pointercancel', release);
  dial.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { rot -= 36; render(); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { rot += 36; render(); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void commit(); }
  });
  render();

  // --- Phone: turn the device itself ----------------------------------------------------------
  let detachMotion = () => {};
  motionBtn.addEventListener('click', async () => {
    const DOE = DeviceOrientationEvent as unknown as OrientationPermission;
    try {
      if (typeof DOE.requestPermission === 'function' && (await DOE.requestPermission()) !== 'granted') throw new Error('denied');
    } catch {
      help.textContent = 'Geen toegang tot de bewegingssensor. Draai dan met je vinger.';
      return;
    }
    method = 'motion';
    help.textContent = 'Leg je gsm plat en draai hem rond, als een slot. Hou even stil op een cijfer.';
    motionBtn.disabled = true;
    let base: number | null = null, startRot = rot, stableSince = performance.now(), lastDigit = currentDigit, travelled = 0;
    const onOrient = (e: DeviceOrientationEvent) => {
      if (e.alpha === null || busy) return;
      if (base === null) { base = e.alpha; startRot = rot; }
      let delta = e.alpha - base;
      if (delta > 180) delta -= 360; if (delta < -180) delta += 360;
      rot = startRot + delta * 1.4;
      render();
      if (currentDigit !== lastDigit) { lastDigit = currentDigit; stableSince = performance.now(); travelled++; }
      if (travelled > 0 && performance.now() - stableSince > 1300) { travelled = 0; void commit(); }
    };
    addEventListener('deviceorientation', onOrient);
    detachMotion = () => removeEventListener('deviceorientation', onOrient);
    backstage.log('sensor', 'Slot: bewegingssensor', 'DeviceOrientation (<code>alpha</code>): de gsm zelf is het slot. Op iOS pas na toestemming én een tik; trillen werkt alleen op Android.');
  });

  // --- Fallback: type the digits ----------------------------------------------------------------
  typeBtn.addEventListener('click', () => {
    method = 'typed';
    const input = el('input', { type: 'text', inputmode: 'numeric', maxlength: code.length, placeholder: '····', autocomplete: 'off', 'aria-label': 'Code' });
    const ok = el('button.btn.btn--primary.btn--small', { type: 'submit' }, 'Open');
    const form = el('form.codeinput', {}, input, ok);
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const v = input.value.replace(/\D/g, '');
      if (v.length === code.length && !busy) { entered = ''; void check(v); input.value = ''; }
    });
    modes.replaceChildren(form);
    input.focus();
  });

  await done;
  detachMotion();
  backstage.log('sensor', 'Opdracht 2: slot open', `Code uit een eerdere opdracht (het jaartal boven de deur). ${attempts} poging${attempts === 1 ? '' : 'en'}, bediend via ${method === 'drag' ? 'vinger (Pointer Events)' : method === 'motion' ? 'de bewegingssensor' : 'intikken'}.`);

  // Reveal what was behind the lock
  sheet.feedback('');
  const next = el('button.btn.btn--primary', { type: 'button' }, 'Verder');
  sheet.body.replaceChildren(el('div.reveal', {},
    el('div.reveal__photo', {}, el('img', { src: '/assets/symbol.jpg', alt: 'Een driehoek met een stip, in krijt op een bakstenen muur' })),
    el('div.reveal__text', { html: 'Dit teken stond overal waar ik kwam. Op de deur. Op de muur achter het archief. Gisteren ook op onze straat.<small>Ellen · map “1898” · 22 dagen geleden</small>' }),
  ), el('div.sheet__actions', {}, next));
  await new Promise<void>((r) => next.addEventListener('click', () => r(), { once: true }));
  await sheet.close();
  return { method, attempts };
}
